import React from 'react';
import get from 'lodash.get';
import { FormattedMessage } from 'react-intl';
import { ofType } from 'redux-observable';
import { EMPTY, from, of } from 'rxjs';
import { catchError, filter, mergeMap, take, takeUntil, map } from 'rxjs/operators';

import { GuidService } from 'ui-components/services/guid-service';
import { ModalService } from 'ui-components/services/modal-service';
import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import { label } from './document-type-epic-helpers/label';
import { isPrintSettingSetToDownload, isPrintSettingSetToPreview } from '../../services/document-service';
import { downloadDocument } from '../documents/documents.helpers';
import { createLocationBarcodeAsync } from './async-printing/location-barcode';
import { createUomBarcodeAsync } from './async-printing/uom-barcode';
import { createPackingSlipsAsync } from './async-printing/packing-slips';
import { createPickListsAsync } from './async-printing/pick-lists';
import { createPurchaseOrderAsync } from './async-printing/purchase-order';
import { handleBulkPrintPusherMessage } from './async-printing/pusher-message-helpers';
import { CUSTOMS_FORM, getLabelDownloadUrl, HOTKEYS_AND_BARCODE, LABEL, PACKING_SLIP, PICK_LIST } from './constants';
import { DocumentTypeEpicHelpers } from './document-type-epic-helpers';
import { messages } from './messages';
import * as Actions from './print.actions';
import * as Api from './print.api';
import { ErrorModal } from '../../components/modals/error-modal';
import { EndOfDayPrintModalContainer } from '../../components/modals/print-modal/end-of-day-print-modal-container';
import { PrintModalContainer } from '../../components/modals/print-modal/print-modal-container';
import { PRINT_LABEL_FINISHED } from '../../constants/analytics-events';
import { PrintEndOfDayWarningContainer } from '../../containers/print-end-of-day-warning-container';
import { PrintLabelPrintWarningContainer } from '../../containers/print-label-print-warning-container';
import { PrintPackingSlipPrintWarningContainer } from '../../containers/print-packing-slip-print-warning-container';
import { UnmarkDocumentAsPrintedWarningContainer } from '../../containers/unmark-document-as-printed-warning-container';
import { emptyArray } from 'ui-components/default-props/empty';
import { CustomsFormStatus } from '../../enums/customs-form-status';
import { DocumentType, PURCHASE_ORDER, LOCATION_BARCODE, UOM_BARCODE } from '../../enums/document-type';
import { FeatureFlags } from '../../feature-flags/feature-flags';
import { DOWNLOAD_PRINT_SETTING_NAME } from '../../pages/settings/printing/constants';
import { Segment } from '../../services/analytics-services/segment';
import { BulkErrorsAndWarningsService } from '../../services/bulk-errors-and-warnings-service';
import { LogService } from '../../services/log-service';
import { presetService } from '../../services/preset-service';
import * as BulkApi from '../bulk/bulk.api';
import { bulkErrorsAndWarningsModalObservable } from '../bulk-errors-and-warnings/bulk-errors-and-warnings.observables';
import * as CustomsActions from '../customs/customs.actions';
import * as DocumentActions from '../documents/documents.actions';
import Global from '../global-selectors';
import { ON_VOID_MODAL_CLOSED } from '../labels/labels.actions';
import { trackLabelPrintingComplete } from '../labels/tracking-events';
import * as PrintSettingsActions from '../print-settings/print-settings.actions';
import * as ConnectActions from '../shipstation-connect/ss-connect.actions';

const getEpicHelperForDocType = (docType) => {
  return DocumentTypeEpicHelpers.find((helper) => helper.id === docType);
};

export const printLabelFromFulfillmentPlanEpic = (action$) => {
  return action$.pipe(
    ofType(Actions.PRINT_LABEL_FROM_FULFILLMENT_PLAN),
    mergeMap((action) => {
      const { hasPrintedLabel, override, autoPrint, fulfillmentPlanId, fulfillmentId } = action;
      if (!fulfillmentPlanId) {
        return EMPTY;
      }
      if (fulfillmentId) {
        return of(Actions.printLabel([fulfillmentPlanId], [fulfillmentId], hasPrintedLabel, override, autoPrint));
      }

      return BulkApi.fulfillmentPlanToFulfillment([fulfillmentPlanId]).pipe(
        mergeMap((data = []) => {
          const fetchedFulfillmentIds = data.map((d) => d.fulfillmentId);
          if (!fetchedFulfillmentIds.length) {
            return EMPTY;
          }

          return of(Actions.printLabel([fulfillmentPlanId], fetchedFulfillmentIds, hasPrintedLabel, override, autoPrint));
        }),
      );
    }),
  );
};

export const openPrintModal = (action$) => {
  return action$.pipe(
    ofType(Actions.PRINT_MODAL_OPEN),
    mergeMap((action) => {
      const { documentType, pdfFilePath, fulfillmentPlanIds, fulfillmentIds, fulfillmentPlanSetIds } = action;
      const modalId = ModalService.launchModal({
        component: PrintModalContainer,
        props: {
          pdfFilePath,
          fulfillmentPlanIds,
          fulfillmentIds,
          fulfillmentPlanSetIds,
          documentType,
        },
        height: '100%',
        width: '100%',
        maxWidth: '100%',
      });
      return action$.pipe(
        filter((nextAction) => {
          return nextAction.type === ON_VOID_MODAL_CLOSED && !nextAction.isCancel;
        }),
        take(1),
        mergeMap(() => {
          ModalService.closeModalById(modalId);
          return of(Actions.closingPrintModal());
        }),
        takeUntil(action$.pipe(ofType(Actions.PRINT_MODAL_CLOSING), take(1))),
      );
    }),
  );
};

export const openPrintModalForEndOfDayForm = (action$) => {
  return action$.pipe(
    ofType(Actions.PRINT_MODAL_OPEN_END_OF_DAY_FORM),
    mergeMap(() => {
      ModalService.launchModal({
        component: EndOfDayPrintModalContainer,
      });
      return EMPTY;
    }),
  );
};

export const closePrintModal = (action$) => {
  return action$.pipe(
    ofType(Actions.PRINT_MODAL_CLOSING),
    mergeMap(() => {
      return EMPTY;
    }),
  );
};

export const documentDownloadStarted = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.DOCUMENT_DOWNLOAD_STARTED),
    mergeMap((action) => {
      const state = getState();
      const rememberSettings = Global.selectPrintRemeberPrintSettings(state);
      const docType = get(action, 'docType');
      const docCount = get(action, 'docCount', 1);
      const baseNotificationProps = {
        notificationType: 'info',
        duration: 2000,
      };
      const doctypeProps = getEpicHelperForDocType(docType).getDownloadStartedProps(docCount);
      NotificationService.showNotification({
        ...baseNotificationProps,
        ...doctypeProps,
      });
      if (rememberSettings) {
        const printer = {
          name: 'download',
          isSyncStation: false,
        };
        const matchingDocType = Object.values(DocumentType).find((doc) => doc.identifier === docType) || {};
        const printSettingKey = matchingDocType.printSettingKey || docType;
        return of(PrintSettingsActions.savePrintSettings(printSettingKey, printer));
      }
      return EMPTY;
    }),
  );
};

export const markPrinted = (action$, { getState }) => {
  return action$.pipe(
    ofType(ConnectActions.PRINT_DOCUMENT_SUCCESS),
    mergeMap((action) => {
      const requestId = get(action, 'requestId', get(action, 'request'));
      const printJobs = Global.selectConnectPrintJobs(getState());
      const printJob = printJobs.find((job) => job.requestId === requestId);
      if (!printJob) {
        return EMPTY;
      }
      const printJobIds = get(printJob, 'ids', emptyArray);
      const printJobRequestId = get(printJob, 'requestId');
      const documentType = get(printJob, 'documentType');
      const docCount = printJobIds.length;
      const printerName = get(printJob, 'printer.name', 'printer');
      if ((documentType !== HOTKEYS_AND_BARCODE && !printJobIds.length) || !printJobRequestId) {
        LogService.logInfo('Print job needs jobId and requestId', {
          printJob,
          action,
        });
        return EMPTY;
      }

      const baseNotificationProps = {
        notificationType: 'info',
        duration: 2000,
      };
      const actionNotificationProps = getEpicHelperForDocType(printJob.documentType).getPrintedSuccessNotificationProps(
        docCount,
        printerName,
      );
      NotificationService.showNotification({
        ...baseNotificationProps,
        ...actionNotificationProps,
      });
      return getEpicHelperForDocType(printJob.documentType).getPrintedSuccessObservable(printJob);
    }),
  );
};

export const markLabelPrinted = (action$) => {
  return action$.pipe(
    ofType(Actions.MARK_LABEL_PRINTED),
    mergeMap((action) => {
      const { isPrinted, override, fulfillmentIds, fulfillmentPlanIds } = action;
      // NOTE: isPrinted passed to this epic represents the desired state, not the current.
      if (!isPrinted && !override) {
        ModalService.launchModal({
          component: UnmarkDocumentAsPrintedWarningContainer,
          props: {
            action,
          },
        });
        return EMPTY;
      }
      return Api.markLabelPrinted(fulfillmentIds, isPrinted).pipe(
        map(() => {
          trackLabelPrintingComplete(fulfillmentPlanIds);
          return Actions.markLabelPrintedSuccess(fulfillmentPlanIds, isPrinted);
        }),
        catchError((error) => {
          return of(Actions.markLabelPrintedFailure(error));
        }),
      );
    }),
  );
};

export const markPackingSlipPrintedIfMatchingLabelHasBeenPrinted = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.MARK_LABEL_PRINTED_SUCCESS),
    mergeMap((action) => {
      const state = getState();
      const labelFormat = Global.selectSellerSellerSetting(state, {
        settingName: 'LabelOutput',
      });
      const labelIncludesPackingSlip = labelFormat.indexOf('-i') !== -1;
      if (!labelIncludesPackingSlip) {
        return EMPTY;
      }
      const { fulfillmentPlanIds, isPrinted } = action;
      return of(Actions.markPackingSlipPrinted(fulfillmentPlanIds, isPrinted));
    }),
  );
};

export const markPackingSlipPrinted = (action$) => {
  return action$.pipe(
    ofType(Actions.MARK_PACKING_SLIP_PRINTED),
    mergeMap((action) => {
      const { isPrinted, override } = action;
      // NOTE: isPrinted passed to this epic represents the desired state, not the current.
      if (!isPrinted && !override) {
        ModalService.launchModal({
          component: UnmarkDocumentAsPrintedWarningContainer,
          props: {
            action,
            docType: PACKING_SLIP,
          },
        });
        return EMPTY;
      }
      const showFailureNotification = () => {
        const errorNotification = label.getAsyncPrintedStatusUpdateError();
        NotificationService.showNotification(errorNotification);
      };

      return Api.markPackingSlipPrinted(action.fulfillmentPlanId, isPrinted).pipe(
        map(() => {
          return Actions.markPackingSlipPrintedSuccess();
        }),
        catchError((error) => {
          showFailureNotification();
          return of(Actions.markPackingSlipPrintedFailure(error));
        }),
      );
    }),
  );
};

export const markPickListPrinted = (action$) => {
  return action$.pipe(
    ofType(Actions.MARK_PICK_LIST_PRINTED),
    mergeMap((action) => {
      const { isPrinted, override } = action;
      // NOTE: isPrinted passed to this epic represents the desired state, not the current.
      if (!isPrinted && !override) {
        ModalService.launchModal({
          component: UnmarkDocumentAsPrintedWarningContainer,
          props: {
            action,
            docType: PICK_LIST,
          },
        });
        return EMPTY;
      }
      const showFailureNotification = () => {
        const errorNotification = label.getAsyncPrintedStatusUpdateError();
        NotificationService.showNotification(errorNotification);
      };

      return Api.markPickListPrinted(action.fulfillmentPlanId, isPrinted).pipe(
        map(() => {
          return Actions.markPickListPrintedSuccess();
        }),
        catchError((error) => {
          showFailureNotification();
          return of(Actions.markPickListPrintedFailure(error));
        }),
      );
    }),
  );
};

export const downloadPackingSlip = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.DOWNLOAD_PACKING_SLIP),
    mergeMap((action) => {
      const { fulfillmentPlanIds, hasPrintedPackingSlip, packingSlipTemplateId, override } = action;

      if (fulfillmentPlanIds.length === 0) {
        return EMPTY;
      }

      const shouldShowReprintWarning = Global.selectPrintSettingsShouldShowPackingSlipRePrintWarning(getState());

      if (hasPrintedPackingSlip && !override && shouldShowReprintWarning) {
        ModalService.launchModal({
          component: PrintPackingSlipPrintWarningContainer,
          props: {
            action,
          },
        });
        return EMPTY;
      }

      return of(DocumentActions.downloadDocument(DocumentType.PackingSlip, fulfillmentPlanIds, packingSlipTemplateId));
    }),
  );
};

export const directPrintPackingSlip = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.DIRECT_PRINT_PACKING_SLIP),
    mergeMap((action) => {
      const { fulfillmentPlanIds, hasPrintedPackingSlip, printSetting, packingSlipTemplateId, override } = action;

      if (fulfillmentPlanIds.length === 0) {
        return EMPTY;
      }

      const shouldShowReprintWarning = Global.selectPrintSettingsShouldShowPackingSlipRePrintWarning(getState());

      if (hasPrintedPackingSlip && !override && shouldShowReprintWarning) {
        ModalService.launchModal({
          component: PrintPackingSlipPrintWarningContainer,
          props: {
            action,
          },
        });
        return EMPTY;
      }

      return of(DocumentActions.printDocument(DocumentType.PackingSlip, fulfillmentPlanIds, printSetting, packingSlipTemplateId));
    }),
  );
};

export const printPackingSlip = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.PRINT_PACKING_SLIP),
    mergeMap((action) => {
      const fulfillmentPlanIds = get(action, 'fulfillmentPlanIds', emptyArray);
      const hasPrintedPackingSlip = get(action, 'hasPrintedPackingSlip', false);
      const documentCount = fulfillmentPlanIds.length;
      if (documentCount === 0) {
        return EMPTY;
      }

      const state = getState();
      const shouldShowReprintWarning = Global.selectPrintSettingsShouldShowPackingSlipRePrintWarning(state);

      if (!action.override && hasPrintedPackingSlip && shouldShowReprintWarning) {
        ModalService.launchModal({
          component: PrintPackingSlipPrintWarningContainer,
          props: {
            action,
          },
        });
        return EMPTY;
      }
      const selectedFormat = Global.selectPrintPrintDocumentFormat(state);
      const selectedTemplateId = selectedFormat?.packingSlipId;
      const packingSlipSetting = Global.selectPrintSettingsPackingSlipSettings(state);
      const isDownloadSetting = packingSlipSetting?.name === DOWNLOAD_PRINT_SETTING_NAME;
      const previouslySelectedPrinter = Global.selectPrintPrintDestination(getState());
      const hasSavedPackingSlipPrinter = packingSlipSetting?.id && packingSlipSetting?.workstationId;
      const printerToUse = hasSavedPackingSlipPrinter ? packingSlipSetting : previouslySelectedPrinter;

      return createPackingSlipsAsync({
        fulfillmentPlanIds,
        getState,
        templateId: selectedTemplateId,
        printer: printerToUse,
      }).pipe(
        mergeMap((printJob) => {
          if (isDownloadSetting) {
            const actions = downloadDocument(printJob.printUrl, DocumentType.PackingSlip, printJob.ids);
            return of(...actions);
          }

          const shouldAutoPrint = (action.autoPrint || hasSavedPackingSlipPrinter) && !!printJob.printer;
          if (shouldAutoPrint) {
            return of(ConnectActions.printDocumentRequest(printJob));
          }
          return of(Actions.openPrintModal(printJob.printUrl, PACKING_SLIP, fulfillmentPlanIds));
        }),
      );
    }),
  );
};

export const downloadLabel = (action$) => {
  return action$.pipe(
    ofType(Actions.DOWNLOAD_LABEL),
    mergeMap((action) => {
      const { fulfillmentIds, hasPrintedLabel, override } = action;

      if (fulfillmentIds.length === 0) {
        return EMPTY;
      }

      if (hasPrintedLabel && !override) {
        ModalService.launchModal({
          component: PrintLabelPrintWarningContainer,
          props: {
            action,
          },
        });
        return EMPTY;
      }

      return of(DocumentActions.downloadDocument(DocumentType.Label, fulfillmentIds));
    }),
  );
};

export const directPrintLabel = (action$) => {
  return action$.pipe(
    ofType(Actions.DIRECT_PRINT_LABEL),
    mergeMap((action) => {
      const { fulfillmentIds, hasPrintedLabel, printSetting, override } = action;

      if (fulfillmentIds.length === 0) {
        return EMPTY;
      }

      if (hasPrintedLabel && !override) {
        ModalService.launchModal({
          component: PrintLabelPrintWarningContainer,
          props: {
            action,
          },
        });
        return EMPTY;
      }

      return of(DocumentActions.printDocument(DocumentType.Label, fulfillmentIds, printSetting));
    }),
  );
};

const printLabelAsyncObservable = ({ downloadUrl, fulfillmentIds, fulfillmentPlanIds, autoPrintObservable }) => {
  if (autoPrintObservable) {
    return of(autoPrintObservable);
  }
  return of(Actions.openPrintModal(downloadUrl, LABEL, fulfillmentPlanIds, fulfillmentIds));
};

const ASYNC_LABEL_PRINT_THRESHOLD = 1;

export const printLabel = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.PRINT_LABEL),
    mergeMap(
      ({ type, fulfillmentPlanIds = [], fulfillmentIds = [], hasPrintedLabel = false, override, autoPrint, shouldNotLaunchPrintModal }) => {
        const state = getState();
        if (window.labelPrintStart) {
          const sellerId = Global.selectSellerSellerId(state);
          const printSettings = Global.selectPrintSettingsLabelPrintSettings(state);
          window.labelPrintFinish = window.performance.now();
          const timeToCompletion = window.labelPrintFinish - window.labelPrintStart;

          Segment.trackEvent(PRINT_LABEL_FINISHED, {
            sellerId,
            timeToCompletionMs: timeToCompletion,
            fulfillmentPlanIds,
            fulfillmentIds,
            printSettings,
          });

          // Reset for future printing
          window.labelPrintStart = undefined;
          window.labelPrintFinish = undefined;
        }
        const documentCount = fulfillmentIds.length;
        if (documentCount === 0) {
          return EMPTY;
        }

        if (!override && hasPrintedLabel) {
          ModalService.launchModal({
            component: PrintLabelPrintWarningContainer,
            props: {
              action: {
                type,
                fulfillmentPlanIds,
                fulfillmentIds,
                hasPrintedLabel,
                override,
                autoPrint,
              },
            },
          });
          return EMPTY;
        }

        const printSettings = Global.selectPrintSettingsLabelPrintSettings(state);
        const hasPrinterId = !!get(printSettings, 'id');

        const { enableV3AsyncLabelPrinting } = new FeatureFlags({ state });
        const isDocumentCountBelowAsyncThreshold = documentCount <= ASYNC_LABEL_PRINT_THRESHOLD;
        const notification = label.getAsyncPrintStartedNotificationProps(documentCount);
        const initialNotificationId =
          enableV3AsyncLabelPrinting && !isDocumentCountBelowAsyncThreshold
            ? NotificationService.showNotification(notification)
            : undefined;

        const showFailureNotification = () => {
          const errorNotification = label.getAsyncPrintFailedNotificationProps(documentCount);
          NotificationService.showNotification(errorNotification);
        };

        const shouldDownload = autoPrint && printSettings && printSettings.name === 'download' && !hasPrinterId;
        if (shouldDownload) {
          return of(Actions.downloadLabel(fulfillmentIds, false));
        }

        if (shouldNotLaunchPrintModal) {
          return EMPTY;
        }

        const printJob = {
          requestId: GuidService.newGuid(),
          printer: printSettings,
          documentType: LABEL,
          ids: fulfillmentIds,
          fulfillmentPlanIds,
        };
        const autoPrintObservable = autoPrint && hasPrinterId ? ConnectActions.printDocumentRequest(printJob) : undefined;

        if (!enableV3AsyncLabelPrinting || isDocumentCountBelowAsyncThreshold) {
          if (autoPrintObservable) {
            return of(autoPrintObservable);
          }
          const url = getLabelDownloadUrl(fulfillmentIds);
          return of(Actions.openPrintModal(url, LABEL, fulfillmentPlanIds, fulfillmentIds));
        }

        return from(Api.generateLabelForFulfillmentsAsync(fulfillmentIds)).pipe(
          mergeMap((jobGuids) =>
            handleBulkPrintPusherMessage({
              jobGuids,
              documentCount,
              initialNotificationId,
              showFailureNotification,
            }),
          ),
          mergeMap(({ downloadUrl }) =>
            printLabelAsyncObservable({
              downloadUrl,
              fulfillmentIds,
              fulfillmentPlanIds,
              autoPrintObservable,
            }),
          ),
          catchError(() => {
            showFailureNotification();
            return EMPTY;
          }),
        );
      },
    ),
  );
};

export const printPickList = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.PRINT_PICK_LIST),
    mergeMap((action) => {
      const fulfillmentPlanIds = get(action, 'fulfillmentPlanIds', emptyArray);
      if (fulfillmentPlanIds.length === 0) {
        return EMPTY;
      }

      const openPrintModalObservable = (url) => of(Actions.openPrintModal(url, PICK_LIST, fulfillmentPlanIds));

      const state = getState();
      const pickListSettings = Global.selectPrintSettingsPickListSettings(state);
      const isDownloadSetting = pickListSettings?.name === DOWNLOAD_PRINT_SETTING_NAME;

      return createPickListsAsync({
        fulfillmentPlanIds,
        isUseCustomerDocType: action.isUseCustomerDocType,
      }).pipe(
        mergeMap((printJob) => {
          if (isDownloadSetting) {
            const actions = downloadDocument(printJob.printUrl, DocumentType.PickList, printJob.ids);
            return of(...actions);
          }
          return openPrintModalObservable(printJob.printUrl);
        }),
      );
    }),
  );
};

export const printCustomsForms = (action$) => {
  return action$.pipe(
    ofType(Actions.PRINT_CUSTOMS_FORMS),
    mergeMap((action) => {
      const fulfillmentPlanIds = get(action, 'fulfillmentPlanIds', emptyArray);
      if (fulfillmentPlanIds.length === 0) {
        return EMPTY;
      }

      return of(CustomsActions.checkCustomsStatusForFulfillmentPlans(fulfillmentPlanIds));
    }),
  );
};

export const printHotkeysAndBarcode = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.PRINT_HOTKEYS_AND_BARCODE),
    mergeMap(() => {
      const state = getState();
      const presets = Global.selectSellerPresets(state) || [];

      const hotkeysAndBarcodeProps = {
        title: <FormattedMessage {...messages.errorGeneratingHotkeys} />,
        content: <FormattedMessage {...messages.hotkeysError} />,
        notificationType: NOTIFICATION_TYPE_ERROR,
      };

      if (presets.length === 0) {
        NotificationService.showNotification(hotkeysAndBarcodeProps);
        return EMPTY;
      }

      const displayHotkeyPrefix = presetService.getHotkeyPrefix();
      const url = `/api/download/Barcodes?keyPrefix=${displayHotkeyPrefix}`;
      return of(Actions.openPrintModal(url, HOTKEYS_AND_BARCODE));
    }),
  );
};

const makeOpenCustomsFormsAction = (shipments) => {
  const fulfillmentPlanIds = shipments.map((shipment) => shipment.fulfillmentPlanId);
  // TODO: Extract this to somewhere that knows about API urls.  The epic should not.
  const url = `/api/download/form/fulfillmentPlan/${fulfillmentPlanIds}`;
  return Actions.openPrintModal(url, CUSTOMS_FORM, fulfillmentPlanIds);
};

export const validatePrintCustomsForms = (action$) => {
  return action$.pipe(
    ofType(CustomsActions.CHECK_CUSTOMS_STATUS_FOR_FULFILLMENT_PLANS_SUCCESS, CustomsActions.CHECK_CUSTOMS_STATUS_FOR_ORDERS_SUCCESS),
    mergeMap(({ customsStatusList, type }) => {
      // todo if it's only one then we should show single flow.

      const allHaveNoErrors = customsStatusList.every((statusItem) => statusItem.status === CustomsFormStatus.HasCustomsFile);
      if (allHaveNoErrors) {
        return of(makeOpenCustomsFormsAction(customsStatusList));
      }

      if (customsStatusList.length === 1) {
        const { title, message } = BulkErrorsAndWarningsService.getSingleCustomsFormError(customsStatusList[0]);
        ModalService.launchModal({
          component: ErrorModal,
          props: {
            title,
            message,
          },
        });
        return EMPTY;
      }

      const tableDisplayType =
        type === CustomsActions.CHECK_CUSTOMS_STATUS_FOR_ORDERS_SUCCESS ? 'fulfillmentPlan' : 'fulfillmentPlanWithoutSalesOrder';
      const errorsAndWarnings = BulkErrorsAndWarningsService.getCustomsFormsErrorsFromCustomsStatusList(customsStatusList);
      const modalTitle = BulkErrorsAndWarningsService.getCustomsFormsTitleByTypeCount(errorsAndWarnings);

      const onContinueAction = (updatedData) => {
        return makeOpenCustomsFormsAction(updatedData.selectedItems);
      };
      return bulkErrorsAndWarningsModalObservable({
        continueCanBeDisabled: true,
        dataType: tableDisplayType,
        errorsAndWarnings,
        fulfillmentPlanSetId: undefined,
        hasAdditionalAction: false,
        hasSelectedItems: !!customsStatusList.length,
        isBatchPrinting: false,
        onContinueAction,
        selectedItems: customsStatusList,
        title: modalTitle,
      });
    }),
  );
};

export const printCustomsFormsForOrders = (action$) => {
  return action$.pipe(
    ofType(Actions.PRINT_CUSTOMS_FORMS_FOR_ORDERS),
    mergeMap(({ salesOrderIds }) => {
      if (salesOrderIds.length === 0) {
        return EMPTY;
      }

      return of(CustomsActions.checkCustomsStatusForOrders(salesOrderIds));
    }),
  );
};

export const printEndOfDayForms = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.PRINT_END_OF_DAY_FORMS),
    mergeMap((action) => {
      const { endOfDayScanFormIds } = action;
      return Api.preflightEndOfDayDownload(endOfDayScanFormIds).pipe(
        mergeMap(() => {
          const printSettings = Global.selectPrintSettingsPrintSettings(getState());
          const endOfDayFormPrintSettings = printSettings[DocumentType.EndOfDayForm.printSettingKey];

          if (isPrintSettingSetToPreview(endOfDayFormPrintSettings)) {
            const url = `${DocumentType.EndOfDayForm.apiDownloadPath}/${endOfDayScanFormIds}`;
            return of(Actions.openPrintModalForEndOfDayForm(url, endOfDayScanFormIds));
          }

          if (isPrintSettingSetToDownload(endOfDayFormPrintSettings)) {
            return of(DocumentActions.downloadDocument(DocumentType.EndOfDayForm, endOfDayScanFormIds));
          }

          return of(DocumentActions.printDocument(DocumentType.EndOfDayForm, endOfDayScanFormIds, endOfDayFormPrintSettings));
        }),
        catchError(() => {
          ModalService.launchModal({
            component: PrintEndOfDayWarningContainer,
          });
          return EMPTY;
        }),
      );
    }),
  );
};

export const printReturnLabels = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.PRINT_RETURN_LABELS),
    mergeMap((action) => {
      const { fulfillmentIds } = action;
      const defaultPrinterSetting = Global.selectPrintSettingsLabelPrintSettings(getState());

      if (defaultPrinterSetting) {
        const printJob = {
          requestId: GuidService.newGuid(),
          printer: defaultPrinterSetting,
          documentType: DocumentType.Label.identifier,
          ids: fulfillmentIds,
        };
        return of(ConnectActions.printDocumentRequest(printJob));
      }
      const url = `${DocumentType.Label.apiDownloadPath}/${fulfillmentIds}`;
      return of(Actions.openPrintModal(url, DocumentType.Label.identifier, undefined, fulfillmentIds));
    }),
  );
};

export const printShipmentManifest = (action$) => {
  return action$.pipe(
    ofType(Actions.PRINT_SHIPMENT_MANIFEST),
    mergeMap((action) => {
      const fulfillmentPlanIds = get(action, 'fulfillmentPlanIds', emptyArray);
      if (fulfillmentPlanIds.length === 0) {
        return EMPTY;
      }

      const url = `${DocumentType.ShipmentManifest.apiDownloadPath}/${fulfillmentPlanIds}`;
      return of(Actions.openPrintModal(url, DocumentType.ShipmentManifest.identifier, fulfillmentPlanIds));
    }),
  );
};

export const printPurchaseOrder = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.PRINT_PURCHASE_ORDER),
    mergeMap((action) => {
      const { purchaseOrderId, printType } = action;
      const previouslySelectedPrinter = Global.selectPrintPrintDestination(getState());
      const openPrintModalObservable = (url) => of(Actions.openPrintModal(url, PURCHASE_ORDER, purchaseOrderId));
      return createPurchaseOrderAsync({
        purchaseOrderId,
        printType,
        printer: previouslySelectedPrinter,
      }).pipe(
        mergeMap((printJob) => {
          return openPrintModalObservable(printJob.printUrl);
        }),
      );
    }),
  );
};

export const printLocationBarcode = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.PRINT_LOCATION_BARCODE),
    mergeMap((action) => {
      const { locationsIds, printType } = action;
      const state = getState();
      const previouslySelectedPrinter = Global.selectPrintPrintDestination(state);
      const locationObjects = locationsIds
        .map((locationId) => Global.selectSellerInventoryWarehouseLocationsById(state, { inventoryWarehouseLocationId: locationId }))
        .filter(Boolean);

      const openPrintModalObservable = (url) => of(Actions.openPrintModal(url, LOCATION_BARCODE, locationsIds));
      return createLocationBarcodeAsync({
        locations: locationObjects,
        printType,
        printer: previouslySelectedPrinter,
      }).pipe(
        mergeMap((printJob) => {
          return openPrintModalObservable(printJob.printUrl);
        }),
      );
    }),
  );
};

export const printUomBarcodes = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.PRINT_UOM_BARCODES),
    mergeMap((action) => {
      const { barcodes } = action;
      const state = getState();
      const previouslySelectedPrinter = Global.selectPrintPrintDestination(state);

      return createUomBarcodeAsync({
        barcodes,
        printer: previouslySelectedPrinter,
      }).pipe(mergeMap((printJob) => of(Actions.openPrintModal(printJob.printUrl, UOM_BARCODE))));
    }),
  );
};
